import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

interface CampoTemplate {
  colonna: string;
  tipo: string;
  obbligatorio: 'Sì' | 'No' | 'Condizionale';
  note?: string;
}

// note: descrizione del campo dal template Kode (foglio "Template Prelievi"/"Template Superamenti
// Reportnet"), seguita dalla regola applicata dalla validazione di G4B quando aggiunge qualcosa
const CAMPI_PRELIEVI: CampoTemplate[] = [
  {
    colonna: 'uuid',
    tipo: 'testo',
    obbligatorio: 'Sì',
    note: 'Identificativo univoco del punto di controllo, creato dalla regione (codice AnTeA, es. 07_010040_AB1_344).'
  },
  {
    colonna: 'dataOraPrelievo',
    tipo: 'data/ora',
    obbligatorio: 'Sì',
    note: 'Data e ora del prelievo del campione, nel formato AAAA-MM-GGTHH:MM:SS.000+01:00 (es. 2025-03-15T09:30:00.000+01:00), dove +01:00 indica il fuso orario italiano.'
  },
  { colonna: 'codicePrelievo', tipo: 'numero intero', obbligatorio: 'Sì', note: 'Numero identificativo del prelievo del campione.' },
  { colonna: 'entePrelievo', tipo: 'testo', obbligatorio: 'Sì', note: "Nome dell'ente o dell'ASL responsabile del prelievo del campione." },
  {
    colonna: 'dataOraAnalisi',
    tipo: 'data/ora',
    obbligatorio: 'Sì',
    note: "Data e ora dell'analisi del campione, nello stesso formato di dataOraPrelievo (AAAA-MM-GGTHH:MM:SS.000+01:00)."
  },
  {
    colonna: 'numeroRapportoProvaAnalisi',
    tipo: 'numero intero',
    obbligatorio: 'Sì',
    note: "Numero identificativo del rapporto di prova relativo all'analisi del campione."
  },
  {
    colonna: 'codiceAccrediaLaboratorioAnalisi',
    tipo: 'testo',
    obbligatorio: 'Sì',
    note: "Codice di accreditamento del laboratorio che ha eseguito l'analisi sul campione."
  },
  { colonna: 'codiceAccrediaLaboratorioEmettitore', tipo: 'testo', obbligatorio: 'No', note: 'Codice di accreditamento del laboratorio appaltatore.' },
  {
    colonna: 'controlloEsterno',
    tipo: 'booleano',
    obbligatorio: 'Sì',
    note: 'Indica se si tratta di un controllo esterno (sì) o interno (no). Valori ammessi: true/false, sì/no, 1/0 o x.'
  },
  {
    colonna: 'controlloExtraPiano',
    tipo: 'booleano',
    obbligatorio: 'Sì',
    note: 'Sì se il controllo effettuato non era previsto nel Programma di Controllo. Stesso formato di controlloEsterno.'
  },
  {
    colonna: 'codiceParametro',
    tipo: 'testo',
    obbligatorio: 'Sì',
    note: 'Codice del parametro analizzato: deve essere tra i Parametri Analitici ammessi. Una riga per ogni parametro del prelievo.'
  },
  {
    colonna: 'unitaMisura',
    tipo: 'testo',
    obbligatorio: 'Condizionale',
    note: 'Unità di misura del valore. Non serve per i parametri qualitativi senza unità di misura.'
  },
  {
    colonna: 'valoreRapportoProva',
    tipo: 'numero',
    obbligatorio: 'Condizionale',
    note: 'Valore riscontrato per il parametro, esattamente come riportato nel rapporto di prova, senza arrotondamenti. Può essere vuoto solo se sono compilati valoreLOQ e LOQ.'
  },
  {
    colonna: 'valoreArrotondato',
    tipo: 'numero',
    obbligatorio: 'Condizionale',
    note: 'Valore arrotondato alla cifra decimale prevista dal D.Lgs. 18/23. Può mancare solo per i parametri qualitativi e per i risultati sotto il LOQ.'
  },
  {
    colonna: 'incertezza',
    tipo: 'numero',
    obbligatorio: 'No',
    note: 'Incertezza di misura associata al valore, solo per i parametri chimici e chimico-fisici. Se non disponibile lasciare vuoto.'
  },
  {
    colonna: 'valoreLOQ',
    tipo: 'testo',
    obbligatorio: 'Condizionale',
    note: 'Indica che il risultato è inferiore al Limite di Quantificazione (LOQ). Accetta solo il simbolo "<"; se valorizzato va compilato anche LOQ.'
  },
  {
    colonna: 'LOQ',
    tipo: 'numero',
    obbligatorio: 'Condizionale',
    note: 'Limite di Quantificazione: valore minimo sotto il quale il parametro non è quantificabile con sicurezza dal metodo. Obbligatorio se valoreLOQ è "<".'
  },
  {
    colonna: 'valoreLFS',
    tipo: 'testo',
    obbligatorio: 'No',
    note: 'Indica che il risultato è oltre il Limite Fiduciario Superiore (LFS). Accetta solo i simboli ">" e "<"; se valorizzato va compilato anche LFS.'
  },
  {
    colonna: 'LFS',
    tipo: 'numero',
    obbligatorio: 'Condizionale',
    note: 'Limite Fiduciario Superiore: concentrazione microbiologica massima rilevabile con il metodo. Obbligatorio se valoreLFS è valorizzato; con "<" deve valere 3 (norma UNI EN ISO 8199:2018).'
  },
  {
    colonna: 'metodo',
    tipo: 'testo',
    obbligatorio: 'No',
    note: 'Metodo di analisi usato per il parametro: codice di un metodo normato (es. ISO) o metodo interno del laboratorio.'
  }
];

const CAMPI_SUPERAMENTI: CampoTemplate[] = [
  {
    colonna: 'uuid',
    tipo: 'testo',
    obbligatorio: 'Sì',
    note: 'Identificativo univoco del punto di controllo, creato dalla regione (codice AnTeA, es. 07_010040_AB1_344).'
  },
  {
    colonna: 'dataOraPrelievo',
    tipo: 'data/ora',
    obbligatorio: 'Sì',
    note: 'Data e ora del prelievo in cui è stato riscontrato il superamento, nel formato AAAA-MM-GGTHH:MM:SS.000+01:00.'
  },
  {
    colonna: 'codiceParametro',
    tipo: 'testo',
    obbligatorio: 'Sì',
    note: 'Parametro che ha superato il range consentito: deve essere tra i Parametri Analitici ammessi.'
  },
  {
    colonna: 'tipologiaPuntoDiConformita',
    tipo: 'testo',
    obbligatorio: 'Sì',
    note: "Tipologia del punto di prelievo in cui è stata riscontrata l'anomalia: uno dei codici Superamenti Reportnet3 ammessi."
  },
  { colonna: 'superamento', tipo: 'booleano', obbligatorio: 'Sì', note: 'Indica se vi è stato o meno un superamento della soglia.' },
  { colonna: 'superamentoNonSignificativo', tipo: 'booleano', obbligatorio: 'Sì', note: 'Indica se il superamento della soglia è non significativo.' },
  {
    colonna: 'superamentoNonSignificativoGiustificazione',
    tipo: 'testo',
    obbligatorio: 'Condizionale',
    note: 'Giustificazione del superamento. Obbligatoria se il superamento è non significativo.'
  },
  {
    colonna: 'superamentoPopolazioneInteressata',
    tipo: 'numero',
    obbligatorio: 'Condizionale',
    note: 'Numero di persone interessate dal superamento. Obbligatorio se il superamento è significativo.'
  },
  {
    colonna: 'superamentoDataInizio',
    tipo: 'data',
    obbligatorio: 'Condizionale',
    note: "Data di inizio del periodo in cui è stata riscontrata l'anomalia. Obbligatoria se il superamento è significativo."
  },
  {
    colonna: 'superamentoDataFine',
    tipo: 'data',
    obbligatorio: 'Condizionale',
    note: "Data di fine del periodo in cui è stata riscontrata l'anomalia. Obbligatoria se il superamento è significativo."
  },
  {
    colonna: 'superamentoCausa',
    tipo: 'testo',
    obbligatorio: 'Condizionale',
    note: 'Causa del superamento: uno dei codici Superamenti Reportnet3 ammessi. Obbligatoria se il superamento è significativo.'
  },
  {
    colonna: 'superamentoAzioneCorrettiva',
    tipo: 'testo',
    obbligatorio: 'Condizionale',
    note: 'Azioni correttive intraprese: uno o più codici Superamenti Reportnet3 ammessi, separati da virgola. Obbligatoria se il superamento è significativo.'
  },
  {
    colonna: 'superamentoAzioneCorrettivaDataInizio',
    tipo: 'data',
    obbligatorio: 'Condizionale',
    note: 'Data di inizio delle azioni correttive indicate. Obbligatoria se il superamento è significativo.'
  },
  {
    colonna: 'superamentoAzioneCorrettivaDataFine',
    tipo: 'data',
    obbligatorio: 'Condizionale',
    note: 'Data di fine delle azioni correttive indicate. Obbligatoria se il superamento è significativo.'
  }
];

/**
 * Popup informativo: elenca le colonne attese nel file Excel da caricare
 * (Controlli/Prelievi o Superamenti Reportnet3), coi rispettivi tipo/obbligatorietà.
 */
@Component({
  selector: 'ebw-monorepo-controlli-template-info-dialog',
  templateUrl: './controlli-template-info-dialog.component.html',
  styleUrls: ['./controlli-template-info-dialog.component.scss']
})
export class ControlliTemplateInfoDialogComponent {
  readonly columns = ['colonna', 'tipo', 'obbligatorio', 'note'];
  readonly campi: CampoTemplate[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { type: 'prelievi' | 'superamenti' },
    private _dialogRef: MatDialogRef<ControlliTemplateInfoDialogComponent>
  ) {
    this.campi = data.type === 'prelievi' ? CAMPI_PRELIEVI : CAMPI_SUPERAMENTI;
  }

  close(): void {
    this._dialogRef.close();
  }
}
